/* ==========================================================================
   ASD cart drawer runtime

   Writes go to the Cart AJAX API, then the drawer's contents are re-rendered
   from the Section Rendering API so prices, discounts, the free-shipping meter
   and the upsell list all come back from Liquid rather than being recomputed
   here. Opening, closing and focus trapping stay with asd-app.js.

   Every write and every re-render runs through one promise chain, and renders
   coalesce: two triggers arriving before the queued render starts collapse into
   a single section fetch. Cart lines are addressed by line key rather than
   index, so a line key read from already-rendered markup still points at the
   same line by the time the request lands.

   Anything in the theme that writes the cart with its own fetch calls
   ASDCart.notify() when it is done. Nothing patches window.fetch.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER_ID = 'asd-cart-drawer';
  var SECTION_ID = 'cart-drawer-section';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function drawer() {
    return document.getElementById(DRAWER_ID);
  }
  /* Shopify's own flows can write to the cart without this script seeing an
     event, so contents are re-read on open. refresh() coalesces, so a caller
     that already asked for one does not cause a second fetch. */
  function openDrawer() {
    if (!window.ASD || !window.ASD.Panels) return;
    window.ASD.Panels.open(DRAWER_ID);
    refresh();
  }

  /* ------------------------------------------------------------------------
     Serial queue — one cart operation at a time, in order
     ------------------------------------------------------------------------ */
  var chain = Promise.resolve();
  var depth = 0;

  function enqueue(task) {
    var run = function () {
      depth++;
      setBusy(true);
      return Promise.resolve()
        .then(task)
        .catch(function (err) {
          console.warn('[asd-cart]', err);
          showError(err && err.message);
        })
        .then(function () {
          depth--;
          if (depth === 0) setBusy(false);
        });
    };
    chain = chain.then(run, run);
    return chain;
  }

  /* Shopify rejects writes the shopper can't see the reason for — stock limits
     above all — so the reason is surfaced in the drawer instead of the console. */
  function errorNode() {
    var el = drawer();
    return el && el.querySelector('[data-asd-cart-error]');
  }

  function showError(message) {
    var node = errorNode();
    if (!node) return;
    node.textContent = message || 'Something went wrong updating your cart.';
    node.hidden = false;
  }

  function clearError() {
    var node = errorNode();
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
  }

  /* aria-busy only. There is deliberately no visual treatment hung off this:
     dimming the drawer while a write was in flight was read as the panel
     reloading on every quantity change. Feedback comes from the optimistic
     quantity update below instead. */
  function setBusy(state) {
    var el = drawer();
    if (!el) return;
    if (state) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  /* ------------------------------------------------------------------------
     Re-render the drawer body from Liquid
     ------------------------------------------------------------------------ */
  function doRefresh() {
    var el = drawer();
    if (!el) return Promise.resolve();

    /* no-store because this is read straight after a cart write: a cached
       section response would hand back the cart as it was before it. */
    return fetch(root() + '?sections=' + SECTION_ID, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var markup = data[SECTION_ID];
        if (!markup) return;

        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = parsed.querySelector('[data-asd-cart-contents]');
        var current = drawer() && drawer().querySelector('[data-asd-cart-contents]');
        if (fresh && current) current.replaceWith(fresh);

        syncHeaderCount();
      });
  }

  /* The drawer title carries the authoritative count after each render, so the
     header badge follows it rather than doing its own cart lookup. */
  function syncHeaderCount() {
    var el = drawer();
    var source = el && el.querySelector('[data-asd-cart-count]');
    if (!source) return;

    var count = parseInt(source.textContent, 10) || 0;
    var badges = document.querySelectorAll('.asd-header__count[data-asd-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].hidden = count === 0;
    }
  }

  /* One queued render at a time. A render that has not started yet already
     picks up whatever the cart looks like when it runs, so a second trigger
     arriving in the meantime has nothing to add and is dropped. This is what
     stops a single add from re-rendering the drawer two or three times. */
  var refreshQueued = false;

  function refresh() {
    if (refreshQueued) return chain;
    refreshQueued = true;
    return enqueue(function () {
      refreshQueued = false;
      return doRefresh();
    });
  }

  /* ------------------------------------------------------------------------
     Cart writes
     ------------------------------------------------------------------------ */
  function postJson(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          throw new Error(data.description || data.message || 'Cart request failed');
        });
      }
      return res.json();
    });
  }

  function changeLine(key, quantity) {
    return enqueue(function () {
      clearError();
      return postJson('cart/change.js', { id: key, quantity: quantity }).then(doRefresh);
    });
  }

  function addVariant(id) {
    return enqueue(function () {
      clearError();
      return postJson('cart/add.js', { items: [{ id: Number(id), quantity: 1 }] }).then(doRefresh);
    });
  }

  /* ------------------------------------------------------------------------
     Delegated clicks inside the drawer
     ------------------------------------------------------------------------ */

  /* The clicked line is updated before the request goes out, so the number
     responds immediately instead of waiting out a section render. The render
     that follows is authoritative and overwrites all of this; the point is
     only that nothing looks frozen in between.

     The steppers carry their target quantity in data-asd-qty, so those have to
     move too, otherwise a second click would re-send the first click's value.
     Their direction comes from data-asd-step rather than the accessible label,
     which is translatable display copy. */
  function paintQuantity(stepper, quantity) {
    var line = stepper.closest('.asd-cline');
    if (!line) return;

    if (quantity <= 0) {
      line.classList.add('asd-cline--removing');
      return;
    }

    var value = line.querySelector('.asd-qty__val');
    if (value) value.textContent = quantity;

    var steppers = line.querySelectorAll('[data-asd-step]');
    for (var i = 0; i < steppers.length; i++) {
      var step = parseInt(steppers[i].getAttribute('data-asd-step'), 10) || 0;
      steppers[i].setAttribute('data-asd-qty', quantity + step);
    }
  }

  document.addEventListener('click', function (e) {
    var target = e.target.closest('[data-asd-qty],[data-asd-cart-add]');
    if (!target) return;

    var el = drawer();
    if (!el || !el.contains(target)) return;

    e.preventDefault();

    if (target.hasAttribute('data-asd-cart-add')) {
      addVariant(target.getAttribute('data-asd-cart-add'));
      return;
    }

    var key = target.getAttribute('data-asd-key');
    var qty = parseInt(target.getAttribute('data-asd-qty'), 10);
    if (!key || isNaN(qty)) return;

    qty = Math.max(0, qty);
    paintQuantity(target, qty);
    changeLine(key, qty);
  });

  /* The header's cart button carries data-panel-open, which asd-app.js handles
     by calling Panels.open() straight away - openDrawer() here is never
     reached, so without this the drawer would open showing whatever the cart
     looked like when the page was served. refresh() coalesces, so the paths
     that do go through openDrawer() still only cause one fetch.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-panel-open="' + DRAWER_ID + '"]');
    if (trigger) refresh();
  });

  /* ------------------------------------------------------------------------
     Adds made elsewhere (product pages, quick add, apps) reach us as the
     storefront standard cart event.

     The name is 'shopify:cart:lines-update'. It is bound as a literal here so
     this works on its own, and scripts.liquid also hands over
     StandardEvents.cartLinesUpdate from @shopify/events, deduped by name, so a
     rename upstream cannot silently break it again. That hand-off has to come
     from a module: this file is a classic script, and import maps only apply
     to module scripts, so it cannot resolve the bare specifier itself.
     ------------------------------------------------------------------------ */
  var boundEvents = {};

  /* Theme settings > Cart > "Automatically open cart drawer", handed over on
     the drawer element. Horizon read this inside its own drawer component,
     which this drawer replaced, so the setting had stopped doing anything. */
  function autoOpenEnabled() {
    var el = drawer();
    return !!el && el.getAttribute('data-asd-auto-open') === 'true';
  }

  /* Whether a cart event was something being added, as opposed to a quantity
     edit or a removal. The settled result carries itemCount and didError; the
     event as dispatched carries action: 'add'. Either is enough, and anything
     unrecognised is treated as an add, which is what every emitter in this
     theme uses the event for. */
  function isAddEvent(event, result) {
    var detail = (result && result.detail) || (event && event.detail);

    if (detail) {
      if (detail.didError) return false;
      if (typeof detail.itemCount === 'number') return detail.itemCount > 0;
      if (detail.action) return detail.action === 'add';
    }
    if (event && event.action) return event.action === 'add';
    return true;
  }

  /* The cart event is dispatched BEFORE the cart write starts, carrying a
     promise that settles once it has finished: product-form.js dispatches and
     only then posts cart/add.js. Refreshing on the bare event therefore re-read
     the cart as it was before the add, which is why the drawer opened empty and
     only filled in on the next open. Settling first is the documented contract
     and what the theme's own cart-icon.js does.

     No debounce around this any more: the promise is what decides when the cart
     is actually readable, and refresh() already coalesces, so a burst of events
     still costs one section fetch. */
  function onExternalCartUpdate(event) {
    var settled = event && event.promise && typeof event.promise.then === 'function'
      ? event.promise
      : null;

    function done(result, failed) {
      if (!failed && autoOpenEnabled() && isAddEvent(event, result)) notify({ open: true });
      else refresh();
    }

    if (!settled) {
      done(null, false);
      return;
    }

    settled.then(
      function (result) { done(result, false); },
      function () { done(null, true); }
    );
  }

  function bindCartEvent(name) {
    if (!name || boundEvents[name]) return;
    boundEvents[name] = true;
    document.addEventListener(name, onExternalCartUpdate);
  }

  bindCartEvent('shopify:cart:lines-update');

  /* ------------------------------------------------------------------------
     Cart writes made elsewhere in the theme

     Horizon's own product form emits the standard event bound above and
     triggers Shopify's openCart action, so it needs nothing here. The ASD
     sections and blocks that post the cart with their own fetch (bundle
     builder, quantity breaks, card quick add) call notify() instead, which is
     the whole of the contract:

       window.ASDCart.notify();              // cart changed, re-read it
       window.ASDCart.notify({ open: true }) // ...and show the drawer

     An earlier build patched window.fetch and XMLHttpRequest to catch an app
     that bypassed the theme's form. That app is gone, and the patches were the
     cause of the repeated re-renders: every write was seen twice, once by the
     patch and once by the standard event.
     ------------------------------------------------------------------------ */
  function notify(options) {
    var opts = options || {};
    return refresh().then(function () {
      if (opts.open) openDrawer();
    });
  }

  /* ------------------------------------------------------------------------
     Route Shopify's openCart action (add-to-cart buttons, apps) to this drawer
     ------------------------------------------------------------------------ */
  function claimOpenCart() {
    var actions = window.Shopify && window.Shopify.actions;
    if (!actions || !actions.openCart || !actions.openCart.configure) return false;

    actions.openCart.configure({
      handler: function () {
        if (drawer()) openDrawer();
        else window.location.href = root() + 'cart';
      }
    });
    return true;
  }

  if (!claimOpenCart()) {
    document.addEventListener('DOMContentLoaded', claimOpenCart, { once: true });
    window.addEventListener('load', claimOpenCart, { once: true });
  }

  window.ASDCart = {
    refresh: refresh,
    notify: notify,
    open: openDrawer,
    add: addVariant,
    change: changeLine,
    /* Called by the module bridge in scripts.liquid with the canonical
       StandardEvents name. Deduped, so binding the same name twice is a no-op. */
    bindCartEvent: bindCartEvent
  };
})();
